import { DateTime } from 'luxon';
import type {
  GeneratedGame,
  GeneratedGameTeamSlot,
} from './GeneratedGame.types';

function getGeneratedGameDatetimeMillis(game: GeneratedGame) {
  const datetime = DateTime.fromISO(game.datetime ?? '');

  return datetime.isValid ? datetime.toMillis() : null;
}

export function getGeneratedGameTeamIds(game: GeneratedGame) {
  const firstTeamId = game.event_teams.data[0]?.team_id ?? null;
  const fallbackAwayTeamId = game.event_teams.data[1]?.team_id ?? null;
  const homeTeamId = game.home_team_id ?? firstTeamId;
  const awayTeamId =
    game.event_teams.data.find(
      (eventTeam) => eventTeam.team_id && eventTeam.team_id !== homeTeamId,
    )?.team_id ?? fallbackAwayTeamId;

  return {
    awayTeamId,
    homeTeamId,
  };
}

export function updateGeneratedGameTeam<TGame extends GeneratedGame>({
  game,
  slot,
  teamId,
}: {
  game: TGame;
  slot: GeneratedGameTeamSlot;
  teamId: string;
}) {
  const { awayTeamId, homeTeamId } = getGeneratedGameTeamIds(game);

  if (!awayTeamId || !homeTeamId) {
    return game;
  }

  const nextHomeTeamId =
    slot === 'home' ? teamId : homeTeamId === teamId ? awayTeamId : homeTeamId;
  const nextAwayTeamId =
    slot === 'away' ? teamId : awayTeamId === teamId ? homeTeamId : awayTeamId;

  return {
    ...game,
    home_team_id: nextHomeTeamId,
    event_teams: {
      data: [
        {
          team_id: nextHomeTeamId,
        },
        {
          team_id: nextAwayTeamId,
        },
      ],
    },
  };
}

export function swapGeneratedGameTeams<TGame extends GeneratedGame>(
  game: TGame,
) {
  const { awayTeamId, homeTeamId } = getGeneratedGameTeamIds(game);

  if (!awayTeamId || !homeTeamId) {
    return game;
  }

  return {
    ...game,
    home_team_id: awayTeamId,
    event_teams: {
      data: [
        {
          team_id: awayTeamId,
        },
        {
          team_id: homeTeamId,
        },
      ],
    },
  };
}

export function sortGeneratedGamesByDatetime<TGame extends GeneratedGame>(
  games: readonly TGame[],
) {
  return games
    .map((game, originalIndex) => ({
      datetimeMillis: getGeneratedGameDatetimeMillis(game),
      game,
      originalIndex,
    }))
    .sort((leftGame, rightGame) => {
      if (
        leftGame.datetimeMillis === null &&
        rightGame.datetimeMillis === null
      ) {
        return leftGame.originalIndex - rightGame.originalIndex;
      }

      if (leftGame.datetimeMillis === null) {
        return 1;
      }

      if (rightGame.datetimeMillis === null) {
        return -1;
      }

      if (leftGame.datetimeMillis === rightGame.datetimeMillis) {
        return leftGame.originalIndex - rightGame.originalIndex;
      }

      return leftGame.datetimeMillis - rightGame.datetimeMillis;
    })
    .map(({ game }) => game);
}
